import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { updateCurrUser } from "../../../../actions/auth";
import { showTeamsSidebarOnTop } from "../../../../actions/latestSidebar";
import { updateAllQueues } from "../../../../actions/queue";
import { storeDefaultTaskValues } from "../../../../actions/task";
import { number, quote, route } from "../../../../config";
import { deleteWorkspace, getTeamSideBarDetails, storeAllQueueProjects, storeQueues, updateProjectQueue, updateQueue } from "../../../../shared/services/queues.service";
import { updateBookmark } from "../../../Bookmarks/bookmarks.service";
import { getDefaultTaskValues } from "../../../Tasks/tasks.service";
import { openTeamSidebar } from "../../../TeamSideBar/teamSideBar.common";
import { getQueueProjects } from "../../QueueTasks/queue.service";
import { getQueueWithUpdatedOwners, getSortedData } from "../queue.helper";

/**
 * Custom hook for managing queue card functionality
 * Provides state management and handlers for queue operations including:
 * - Queue navigation and view changes
 * - Queue name editing and saving
 * - Owner management
 * - Bookmark functionality
 * - Project movement between queues
 * - Search and sorting capabilities
 *
 * @param {Array} gridData - Array of queue data objects containing queue information
 * @returns {Object} Object containing state variables, refs, and handler functions
 * @author Bhavana
 */
export const useQueueCard = (gridData) => {
  // State management
  const [showMore, setShowMore] = useState([]);
  const [sortConfigs, setSortConfigs] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [openAddTeamCardIndex, setOpenAddTeamCardIndex] = useState(null);
  const [queueNameEditIndex, setQueueNameEditIndex] = useState(null);
  const [queueNameEdits, setQueueNameEdits] = useState({});
  const [openMovePopup, setOpenMovePopup] = useState({ index: null, teamIndex: null });
  const [queue, setQueue] = useState({});
  const [openEditOwnerPopup, setOpenEditOwnerPopup] = useState(null);
  const [openMoreMenuPopup, setOpenMoreMenuPopup] = useState(null);
  const [bookmarkStates, setBookmarkStates] = useState({});
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [hoveredQueueName, setHoveredQueueName] = useState(null);
  const [hoveredMembersArea, setHoveredMembersArea] = useState(null);
  const [hoveredTableRow, setHoveredTableRow] = useState(null);

  // Refs
  const addTeamButtonRefs = useRef({});
  const moveIconRefs = useRef({});
  const editOwnerRefs = useRef({});
  const moreMenuRefs = useRef({});

  // Redux selectors
  const allQueues = useSelector((state) => state.allQueues);
  const { allQueueProjects } = useSelector((state) => state.queueProjects) || [];
  const { user } = useSelector((state) => state.auth);
  const { assigneeList, allProjectsList, tasks } = useSelector((state) => state?.tasks?.defaultDetails);

  const { queueBookmarks } = useSelector((state) => state.bookmarks);

  const dispatch = useDispatch();
  const history = useHistory();

  /**
   * Effect to get default task values when user data changes
   * Dispatches action to fetch default task values for the current user
   *
   * @author Bhavana
   */
  useEffect(() => {
    if (user?.companyId && user?.id) {
      dispatch(getDefaultTaskValues(user?.companyId, user?.id));
    }
  }, [user?.companyId, user?.id, dispatch]);

  /**
   * Effect to update bookmark states when queue bookmarks or grid data changes
   * Creates a mapping of queue IDs to their bookmark states
   *
   * @author Bhavana
   */
  useEffect(() => {
    if (queueBookmarks && gridData) {
      const newBookmarkStates = {};
      gridData.forEach((gridRow) => {
        const queueId = gridRow?.QueueId;
        newBookmarkStates[queueId] = queueBookmarks?.find((bookmark) => bookmark?.Id === queueId);
      });
      setBookmarkStates(newBookmarkStates);
    }
  }, [queueBookmarks, gridData]);

  /**
   * Effect to handle click outside for more menu popup
   * Adds event listener to close popup when clicking outside
   *
   * @author Bhavana
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMoreMenuPopup !== null) {
        const popupElement = document.querySelector(".more-menu-popup");
        const moreButtonElement = moreMenuRefs.current[openMoreMenuPopup]?.current;

        if (popupElement && !popupElement.contains(event.target) && moreButtonElement && !moreButtonElement.contains(event.target)) {
          setOpenMoreMenuPopup(null);
        }
      }
    };

    if (openMoreMenuPopup !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMoreMenuPopup]);

  /**
   * Navigate to team task view for a specific project
   *
   * @param {Object} project - Project object containing ProjectId and ProjectName
   * @author Bhavana
   */
  const changeTeamTaskView = async (project) => {
    history.push(`${route.PRIVATE_ROUTE.TASKS.PATH}/${project?.ProjectId}/${encodeURIComponent(project?.ProjectName)}`);
    await dispatch(updateCurrUser({ lastSelectedBoard: "Grid" }));
  };

  /**
   * Navigate to task view for a specific queue
   *
   * @param {string} value - View type identifier
   * @param {Object} queue - Queue object containing QueueId and QueueName
   * @author Bhavana
   */
  const changeTaskView = async (value, queue) => {
    history.push(`${route.PRIVATE_ROUTE.QUEUE_TASKS.PATH}/${queue?.QueueId}/${encodeURIComponent(queue?.QueueName)}`);
    await dispatch(updateCurrUser({ lastSelectedBoard: value }));
  };

  /**
   * Handle queue projects by dispatching actions to fetch queue and default task data
   *
   * @param {Object} queue - Queue object containing QueueId
   * @author Bhavana
   */
  const handleQueueProjects = (queue) => {
    const payload = {
      companyId: user?.companyId,
      queueId: queue?.QueueId,
    };
    dispatch(getQueueProjects(payload));
  };

  /**
   * Handle edit workspace by opening team sidebar and fetching queue projects
   *
   * @param {Object} queue - Queue object containing QueueId
   * @author Bhavana
   */
  const handleEditWorkspace = (queue) => {
    dispatch(storeQueueProjects([]));
    openTeamSidebar(queue?.QueueId);
    dispatch(showTeamsSidebarOnTop());
    handleQueueProjects(queue);
    dispatch(getDefaultTaskValues(user?.companyId, user?.id));
  };

  /**
   * Handle view change by either navigating to task view or editing workspace
   *
   * @param {string} value - View type identifier
   * @param {Object} queue - Queue object
   * @author Bhavana
   */
  const handleViewChange = (value, queue) => {
    if (value) changeTaskView(value, queue);
    else handleEditWorkspace(queue);
  };

  /**
   * Handle show more functionality to toggle visibility of additional content
   *
   * @param {number} clickedIndex - Index of the clicked item
   * @author Bhavana
   */
  const handleShowMore = useCallback(
    (clickedIndex) => {
      if (showMore?.includes(clickedIndex)) {
        let filteredVisibleRows = [...showMore]?.filter((i) => i !== clickedIndex);
        return setShowMore(filteredVisibleRows);
      }
      setShowMore([...showMore, clickedIndex]);
    },
    [showMore]
  );

  /**
   * Handle project click by navigating to team settings
   *
   * @param {Object} project - Project object containing ProjectId
   * @author Bhavana
   */
  const handleProjectClick = useCallback(
    (project) => {
      history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project?.ProjectId}`);
    },
    [history]
  );

  /**
   * Handle search change by updating search terms for a specific card
   *
   * @param {number} cardIndex - Index of the card
   * @param {string} searchValue - Search input value
   * @author Bhavana
   */
  const handleSearchChange = useCallback((cardIndex, searchValue) => {
    setSearchTerms((prev) => ({
      ...prev,
      [cardIndex]: searchValue,
    }));
  }, []);

  /**
   * Handle queue name save by updating queue name if changed
   *
   * @param {number} index - Index of the queue in grid data
   * @param {string} queueId - ID of the queue to update
   * @author Bhavana
   */
  const handleQueueNameSave = useCallback(
    (index, queueId) => {
      const newName = queueNameEdits[index];
      const originalQueue = gridData[index];

      if (newName && newName.trim() !== "" && newName?.trim() !== originalQueue?.QueueName) {
        dispatch(updateQueue({ queueId: queueId, queueName: newName }));
        const updatedQueue = { ...originalQueue, QueueName: newName };
        dispatch(updateAllQueues(updatedQueue));

        /* Update allProjectsList to reflect the new queue name for teamSetting breadcrumb */
        const updatedAllProjectsList = allProjectsList.map((project) => (project.QueueId === queueId ? { ...project, QueueName: newName } : project));
        dispatch(storeDefaultTaskValues({ ...tasks, allProjectsList: updatedAllProjectsList }));
      }
      setQueueNameEditIndex(null);
    },
    [dispatch, queueNameEdits, gridData]
  );

  /**
   * Handle edit owner click by setting the edit owner popup index
   *
   * @param {number} index - Index of the queue to edit owners for
   * @author Bhavana
   */
  const handleEditOwnerClick = useCallback((index) => {
    setOpenEditOwnerPopup(index);
  }, []);

  /**
   * Handle save owners by updating queue owners
   *
   * @param {number} index - Index of the queue in grid data
   * @param {Array} newOwners - Array of new owner objects
   * @author Bhavana
   */
  const handleSaveOwners = useCallback(
    (index, newOwners) => {
      const queueId = gridData[index]?.QueueId;
      if (queueId && newOwners && newOwners.length > 0) {
        const ownerIds = newOwners.map((owner) => owner?.value?.toString());
        const payload = {
          queueId: queueId,
          owners: ownerIds,
        };
        dispatch(updateQueue(payload));
        const updatedQueue = getQueueWithUpdatedOwners(gridData[index], newOwners, payload, user?.id);
        dispatch(updateAllQueues(updatedQueue));
      }
      setOpenEditOwnerPopup(null);
    },
    [dispatch, gridData, user?.id]
  );

  /**
   * Handle delete workspace by confirming and dispatching delete action
   *
   * @param {number} index - Index of the queue to delete
   * @author Bhavana
   */
  const handleDeleteWorkspace = useCallback(
    (index) => {
      const queueId = gridData[index]?.QueueId;
      const queueName = gridData[index]?.QueueName;

      if (queueId && window.confirm(`${quote.DELETE_WORKSPACE} "${queueName}"? ${quote.DELETE_WORKSPACE_CONFIRMATION}`)) {
        const payload = {
          user: user,
          queueId: queueId,
        };
        dispatch(deleteWorkspace(payload));
      }
      setOpenMoreMenuPopup(null);
    },
    [dispatch, gridData, user]
  );

  /**
   * Handle bookmark function by toggling bookmark state for a queue
   *
   * @param {string} queueId - ID of the queue to bookmark/unbookmark
   * @author Bhavana
   */
  const handleBookmarkFunction = useCallback(
    async (queueId) => {
      const isBookmarked = bookmarkStates[queueId];

      if (isBookmarked || queueBookmarks?.length < number?.TEN) {
        const payload = {
          userId: user?.id,
          entityType: "Queue",
          entityId: queueId,
          sortId: number?.ZERO,
        };

        const teamPayload = {
          userId: user?.id,
          companyId: user?.companyId,
          queueId: queueId,
        };
        await dispatch(getTeamSideBarDetails(teamPayload));
        await dispatch(updateBookmark(payload));
      }
    },
    [bookmarkStates, queueBookmarks, user, dispatch]
  );

  /**
   * Change project queue by moving a project from one queue to another
   * Updates both queue data and project data accordingly
   *
   * @author Bhavana
   */
  const changeProjectQueue = useCallback(() => {
    const currentCardIndex = openMovePopup?.index;
    const currentTeamIndex = openMovePopup?.teamIndex;
    if (currentCardIndex === null || currentTeamIndex === null) return;

    const gridRow = gridData[currentCardIndex];
    const sortedDetails = getSortedData(gridRow?.details, currentCardIndex, sortConfigs, searchTerms[currentCardIndex] || "");
    const projectId = sortedDetails[currentTeamIndex]?.ProjectId;

    if (!queue?.QueueId || !gridRow?.QueueId || !projectId) return;

    const payload = {
      data: {
        queueId: queue?.QueueId,
        previousQueue: gridRow?.QueueId,
      },
      projects: [projectId],
    };
    dispatch(updateProjectQueue(payload));

    const updatedQueues = allQueues?.map((queue) => {
      if (queue?.QueueId === gridRow?.QueueId) {
        return {
          ...queue,
          details: queue?.details?.filter((proj) => proj?.ProjectId !== projectId),
        };
      }
      if (queue?.QueueId === queue?.QueueId) {
        return {
          ...queue,
          details: [...(queue?.details || []), sortedDetails[currentTeamIndex]],
        };
      }
      return queue;
    });

    dispatch(storeQueues(updatedQueues));

    const updatedAllQueueProjects = allQueueProjects?.map((proj) => {
      if (proj?.ProjectId === projectId) {
        return { ...proj, QueueId: queue?.QueueId };
      }
      return proj;
    });
    dispatch(storeAllQueueProjects(updatedAllQueueProjects));

    setOpenMovePopup({ index: null, teamIndex: null });
    setQueue({});
  }, [dispatch, gridData, openMovePopup, queue, sortConfigs, searchTerms, allQueues, allQueueProjects]);

  return {
    // State
    showMore,
    sortConfigs,
    setSortConfigs,
    searchTerms,
    openAddTeamCardIndex,
    setOpenAddTeamCardIndex,
    queueNameEditIndex,
    setQueueNameEditIndex,
    queueNameEdits,
    setQueueNameEdits,
    openMovePopup,
    setOpenMovePopup,
    queue,
    setQueue,
    openEditOwnerPopup,
    setOpenEditOwnerPopup,
    openMoreMenuPopup,
    setOpenMoreMenuPopup,
    bookmarkStates,
    hoveredCardIndex,
    setHoveredCardIndex,
    hoveredQueueName,
    setHoveredQueueName,
    hoveredMembersArea,
    setHoveredMembersArea,
    hoveredTableRow,
    setHoveredTableRow,

    // Refs
    addTeamButtonRefs,
    moveIconRefs,
    editOwnerRefs,
    moreMenuRefs,

    // Redux state
    allQueues,
    allQueueProjects,
    user,
    assigneeList,
    queueBookmarks,

    // Handlers
    changeTeamTaskView,
    changeTaskView,
    handleQueueProjects,
    handleEditWorkspace,
    handleViewChange,
    handleShowMore,
    handleProjectClick,
    handleSearchChange,
    handleQueueNameSave,
    handleEditOwnerClick,
    handleSaveOwners,
    handleDeleteWorkspace,
    handleBookmarkFunction,
    changeProjectQueue,
  };
};
